import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Clock,
  AlertOctagon,
  AlertTriangle,
  Eye,
  Upload,
  Download,
  X,
  FileText,
  Filter,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  CalendarClock,
  Search,
  FileDown,
  Percent,
} from "lucide-react";
import { db, storage } from "../firebaseConfig";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  addDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import Modal from "../components/Modal";

// ======= Utils =======
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const isPast = (iso) => !!iso && new Date(iso) < startOfDay(new Date());
const isNear = (iso, days = 7) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = startOfDay(new Date());
  const limit = new Date(now);
  limit.setDate(limit.getDate() + days);
  return d >= now && d <= limit;
};
const fmtEUR = (n) =>
  (typeof n === "number" ? n : 0).toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });
const fmtPlain = (n, currency = "EUR") =>
  (typeof n === "number" ? n : 0).toLocaleString("es-ES", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  });
const todayISO = () => new Date().toISOString().slice(0, 10);

function sum(arr, f = (x) => x) {
  return arr.reduce((acc, it) => acc + (Number(f(it)) || 0), 0);
}

// compute totals in EUR, assuming invoice may carry currency & exchangeRate
function invoiceTotalEUR(inv) {
  if (typeof inv.totalGrossEUR === "number") return inv.totalGrossEUR;
  if (typeof inv.totalGross === "number") {
    if (inv.currency && inv.currency !== "EUR" && inv.exchangeRate)
      return inv.totalGross / (Number(inv.exchangeRate) || 1);
    return inv.totalGross;
  }
  // fallback: base + vat - irpf
  const base = Number(inv.totalBase) || 0;
  const vat = Number(inv.vatAmount) || 0;
  const irpf = Number(inv.irpfAmount) || 0;
  const gross = base + vat - irpf;
  if (inv.currency && inv.currency !== "EUR" && inv.exchangeRate)
    return gross / (Number(inv.exchangeRate) || 1);
  return gross;
}

function computePaidEUR(inv) {
  const ps = Array.isArray(inv.payments) ? inv.payments : [];
  // Payments may be in different currencies; assume all payments already normalized to EUR (recommended),
  // otherwise map by payment.currency & exchangeRate:
  return sum(ps, (p) => {
    if (typeof p.amountEUR === "number") return p.amountEUR;
    if (p.currency && p.currency !== "EUR" && p.exchangeRate)
      return Number(p.amount || 0) / (Number(p.exchangeRate) || 1);
    return Number(p.amount || 0);
  });
}

function computeStatus(inv) {
  const total = invoiceTotalEUR(inv);
  const paid = computePaidEUR(inv);
  const due = new Date(inv.dueDate || inv.due_date || inv.paymentDue);
  if (paid >= total - 0.005) return "paid";
  if (paid > 0 && paid < total - 0.005) return "partial";
  if (paid <= 0.005 && due < startOfDay(new Date())) return "overdue";
  return "pending";
}

function statusColor(status) {
  if (status === "paid") return "#22c55e";
  if (status === "partial") return "#f59e0b";
  if (status === "overdue") return "#dc2626";
  return "var(--muted-foreground)";
}

function DueBadge({ dueDate }) {
  const expired = isPast(dueDate);
  const near = isNear(dueDate, 7);
  const color = expired ? "#dc2626" : near ? "#f59e0b" : "#111827";
  return (
    <span style={{ fontWeight: 600, color }}>
      {dueDate ? new Date(dueDate).toLocaleDateString("es-ES") : "—"}
    </span>
  );
}

function StatusDot({ status }) {
  const color = statusColor(status);
  const Icon =
    status === "paid"
      ? CheckCircle2
      : status === "overdue"
      ? AlertOctagon
      : status === "partial"
      ? AlertTriangle
      : Clock;
  return (
    <span
      title={status}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <Icon size={16} color={color} />
    </span>
  );
}

function PayProgress({ paid, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  return (
    <div style={{ minWidth: 140 }}>
      <div
        style={{
          fontSize: 12,
          color: "var(--muted-foreground)",
          marginBottom: 4,
        }}
      >
        Pagado {fmtEUR(paid)} / {fmtEUR(total)} ({pct}%)
      </div>
      <div style={{ height: 8, background: "#e5e7eb", borderRadius: 999 }}>
        <div
          style={{
            width: `${pct}%`,
            height: 8,
            background: pct >= 100 ? "#22c55e" : "#3b82f6",
            borderRadius: 999,
            transition: "width .2s ease",
          }}
        />
      </div>
    </div>
  );
}

// ======= Modales =======

function PaymentUploadModal({
  open,
  onClose,
  projectCode,
  invoices,
  defaultSupplier,
  onSaved,
}) {
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [exchangeRate, setExchangeRate] = useState("");
  const [type, setType] = useState("invoice");
  const [supplierName, setSupplierName] = useState(defaultSupplier || "");
  const [refBank, setRefBank] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});

  useEffect(() => {
    setSupplierName(defaultSupplier || "");
  }, [defaultSupplier]);

  const handleToggleInvoice = (id) => {
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAlloc = (id, v) => {
    setAllocations((prev) => ({ ...prev, [id]: v }));
  };

  const uploadFile = async () => {
    if (!file) return { fileUrl: "", fileName: "" };
    const path = `projects/${projectCode}/payments/${Date.now()}_${file.name}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    const url = await getDownloadURL(ref);
    return { fileUrl: url, fileName: file.name };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { fileUrl, fileName } = await uploadFile();
    const amountNum = Number(amount || 0);
    const allocs = Object.entries(allocations)
      .filter(([id, val]) => selectedInvoices.includes(id) && Number(val) > 0)
      .map(([invoiceId, val]) => ({
        invoiceId,
        amount: Number(val),
      }));

    const payload = {
      date,
      amount: amountNum,
      currency,
      exchangeRate: exchangeRate ? Number(exchangeRate) : null,
      invoices: selectedInvoices,
      allocations: allocs,
      supplierName,
      type,
      refBank,
      notes,
      fileUrl,
      fileName,
      createdAt: serverTimestamp(),
    };

    await addDoc(collection(db, "projects", projectCode, "payments"), payload);
    onSaved?.();
    onClose?.();
  };

  if (!open) return null;

  return (
    <Modal
      title="Registrar pago / justificante"
      onClose={onClose}
      style={{
        maxWidth: "1200px",
        width: "95%",
        maxHeight: "90vh",
        overflowY: "auto",
        paddingBottom: 16,
      }}
    >
      {/* cuerpo con scroll */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
          boxSizing: "border-box",
          scrollbarWidth: "thin",
          scrollbarColor: "var(--muted-foreground) transparent",
        }}
      >
        <form
          id="paymentForm"
          className="stack"
          onSubmit={handleSave}
          style={{
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* === campos principales === */}
          <div className="form-grid">
            <div className="field">
              <label>Fecha de pago</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Importe</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Moneda</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option>EUR</option>
                <option>USD</option>
                <option>GBP</option>
              </select>
            </div>
            <div className="field">
              <label>Tipo</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="invoice">Pago de factura</option>
                <option value="partial">Pago parcial</option>
                <option value="deposit">Depósito / Anticipo</option>
                <option value="proforma">Proforma / Presupuesto</option>
              </select>
            </div>
            <div className="field">
              <label>Proveedor</label>
              <input
                type="text"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Nombre fiscal del proveedor"
              />
            </div>
            <div className="field field--span2">
              <label>Referencia bancaria</label>
              <input
                type="text"
                value={refBank}
                onChange={(e) => setRefBank(e.target.value)}
                placeholder="Código transferencia / concepto"
              />
            </div>
            <div className="field field--span2">
              <label>Notas</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones"
              />
            </div>
            <div className="field">
              <label>Cambio (opcional)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  step="0.0001"
                  placeholder="1.0"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                />
                <span title="Si la moneda no es EUR, ratio para pasar a EUR.">
                  <Percent size={14} />
                </span>
              </div>
            </div>
            <div className="field field--span2">
              <label>Justificante (PDF/imagen)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          {/* === asignar a facturas === */}
          <div className="stack" style={{ gap: 8, marginTop: 8 }}>
            <div
              style={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 8,
                position: "sticky",
                top: 0,
                background: "var(--background)",
                zIndex: 2,
                padding: "4px 0",
              }}
            >
              <FileText size={16} /> Asignar a facturas
            </div>
            <div
              className="card"
              style={{
                padding: 12,
                border: "1px solid var(--ring)",
                borderRadius: 10,
                maxHeight: 320,
                overflowY: "auto",
              }}
            >
              {invoices.length === 0 && (
                <div style={{ color: "var(--muted-foreground)" }}>
                  No hay facturas para asignar.
                </div>
              )}
              {invoices.map((inv) => {
                const total = invoiceTotalEUR(inv);
                const paid = computePaidEUR(inv);
                const pending = Math.max(0, total - paid);
                const checked = selectedInvoices.includes(inv.id);
                return (
                  <div
                    key={inv.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "20px 1fr 120px 120px 120px",
                      gap: 10,
                      alignItems: "center",
                      padding: "6px 0",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleInvoice(inv.id)}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {inv.supplierName || inv.supplier || "Proveedor"} —{" "}
                        {inv.number || inv.invoiceNumber || inv.id}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--muted-foreground)",
                        }}
                      >
                        Fecha:{" "}
                        {inv.issueDate
                          ? new Date(inv.issueDate).toLocaleDateString("es-ES")
                          : "—"}{" "}
                        · Venc.: <DueBadge dueDate={inv.dueDate} />
                      </div>
                    </div>
                    <div style={{ fontFamily: "monospace" }}>
                      Total: {fmtEUR(total)}
                    </div>
                    <div style={{ fontFamily: "monospace" }}>
                      Pagado: {fmtEUR(paid)}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontFamily: "monospace" }}>Asignar:</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={pending.toFixed(2)}
                        value={allocations[inv.id] ?? ""}
                        onChange={(e) => handleAlloc(inv.id, e.target.value)}
                        disabled={!checked}
                        style={{ width: 110 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ height: 12 }} />
        </form>
      </div>

      {/* pie fijo */}
      <div
        style={{
          padding: "12px 20px",
          borderTop: "1px solid var(--ring)",
          background: "var(--background)",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <button type="button" className="button" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="submit"
          form="paymentForm"
          className="button button--primary"
        >
          Guardar pago
        </button>
      </div>
    </Modal>
  );
}

function InvoiceDetailsModal({ open, invoice, onClose }) {
  if (!open || !invoice) return null;
  const total = invoiceTotalEUR(invoice);
  const paid = computePaidEUR(invoice);
  const status = computeStatus(invoice);
  return (
    <Modal
      title="Detalle de factura"
      onClose={onClose}
      style={{ maxWidth: 900 }}
    >
      <div className="stack">
        <div className="card" style={{ padding: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <StatusDot status={status} />
              <div style={{ fontWeight: 700 }}>
                {invoice.supplierName || invoice.supplier || "Proveedor"} —{" "}
                {invoice.number || invoice.invoiceNumber || invoice.id}
              </div>
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
              {fmtEUR(total)}
            </div>
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--muted-foreground)",
              marginTop: 6,
            }}
          >
            Fecha:{" "}
            {invoice.issueDate
              ? new Date(invoice.issueDate).toLocaleDateString("es-ES")
              : "—"}{" "}
            · Vencimiento: <DueBadge dueDate={invoice.dueDate} />
          </div>
          <div style={{ marginTop: 10 }}>
            <PayProgress paid={paid} total={total} />
          </div>
          {invoice.currency && invoice.currency !== "EUR" && (
            <div
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                marginTop: 6,
              }}
            >
              Original:{" "}
              {fmtPlain(invoice.totalGross || total, invoice.currency)}{" "}
              {invoice.exchangeRate ? `@ ${invoice.exchangeRate}` : ""}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 12 }}>
          <div
            style={{
              fontWeight: 700,
              marginBottom: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <CreditCard size={16} /> Pagos asociados
          </div>
          <div className="stack">
            {(invoice.payments || []).length === 0 && (
              <div style={{ color: "var(--muted-foreground)" }}>
                Sin pagos registrados.
              </div>
            )}
            {(invoice.payments || []).map((p) => (
              <div
                key={p.id || p.refBank || Math.random()}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {p.date
                      ? new Date(p.date).toLocaleDateString("es-ES")
                      : "—"}{" "}
                    · {p.refBank || "sin referencia"}
                  </div>
                  <div
                    style={{ fontSize: 12, color: "var(--muted-foreground)" }}
                  >
                    {p.type || "invoice"}{" "}
                    {p.currency && p.currency !== "EUR"
                      ? ` · ${fmtPlain(p.amount || 0, p.currency)}${
                          p.exchangeRate ? ` @ ${p.exchangeRate}` : ""
                        }`
                      : ""}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
                    {typeof p.amountEUR === "number"
                      ? fmtEUR(p.amountEUR)
                      : fmtEUR(
                          p.currency && p.currency !== "EUR" && p.exchangeRate
                            ? Number(p.amount || 0) /
                                Number(p.exchangeRate || 1)
                            : Number(p.amount || 0)
                        )}
                  </div>
                  {p.fileUrl && (
                    <a
                      className="button"
                      href={p.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Descargar justificante"
                    >
                      <Download size={16} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {invoice.fileUrl && (
          <div className="card" style={{ padding: 12 }}>
            <div
              style={{
                fontWeight: 700,
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FileText size={16} /> Archivo factura
            </div>
            <a
              className="button"
              href={invoice.fileUrl}
              target="_blank"
              rel="noreferrer"
            >
              Abrir PDF
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
}

function PaymentDetailsModal({ open, payment, onClose }) {
  if (!open || !payment) return null;
  return (
    <Modal title="Detalle de pago" onClose={onClose} style={{ maxWidth: 900 }}>
      <div className="stack">
        <div className="card" style={{ padding: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CheckCircle2 size={16} color="#22c55e" />
              <div style={{ fontWeight: 700 }}>
                {payment.supplierName || "Proveedor"} —{" "}
                {payment.refBank || "Referencia"}
              </div>
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
              {payment.currency && payment.currency !== "EUR"
                ? fmtPlain(payment.amount || 0, payment.currency)
                : fmtEUR(payment.amount || 0)}
            </div>
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--muted-foreground)",
              marginTop: 6,
            }}
          >
            Fecha:{" "}
            {payment.date
              ? new Date(payment.date).toLocaleDateString("es-ES")
              : "—"}{" "}
            · Tipo: {payment.type}
          </div>
          {payment.fileUrl && (
            <div style={{ marginTop: 10 }}>
              <a
                className="button"
                href={payment.fileUrl}
                target="_blank"
                rel="noreferrer"
              >
                Descargar justificante
              </a>
            </div>
          )}
          {(payment.allocations || []).length > 0 && (
            <div
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                marginTop: 8,
              }}
            >
              Asignaciones:{" "}
              {(payment.allocations || [])
                .map((a) => `${a.invoiceId}:${a.amount}`)
                .join(", ")}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ======= Componentes fila =======

function InvoiceRow({ inv, onOpen }) {
  const total = invoiceTotalEUR(inv);
  const paid = computePaidEUR(inv);
  const pending = Math.max(0, total - paid);
  const status = computeStatus(inv);
  const expired = isPast(inv.dueDate);
  const near = isNear(inv.dueDate, 7);

  return (
    <div className="card" style={{ padding: 12 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "28px 110px 130px 1fr 160px 220px 100px",
          gap: 12,
          alignItems: "center",
        }}
      >
        <FileText size={18} />
        <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
          {inv.issueDate
            ? new Date(inv.issueDate).toLocaleDateString("es-ES")
            : "—"}
        </div>
        <div
          title="Vencimiento"
          style={{
            fontWeight: 700,
            color: expired ? "#dc2626" : near ? "#f59e0b" : "inherit",
          }}
        >
          <CalendarClock
            size={14}
            style={{ verticalAlign: "text-bottom", marginRight: 6 }}
          />
          <DueBadge dueDate={inv.dueDate} />
        </div>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {inv.supplierName || inv.supplier || "Proveedor"} —{" "}
          {inv.number || inv.invoiceNumber || inv.id}
        </div>
        <div
          style={{
            fontFamily: "monospace",
            fontWeight: 700,
            textAlign: "right",
          }}
        >
          {fmtEUR(total)}
        </div>
        <div>
          <PayProgress paid={paid} total={total} />
        </div>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <StatusDot status={status} />
          <button
            className="button button--ghost"
            onClick={() => onOpen(inv)}
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
        </div>
      </div>
      {inv.currency && inv.currency !== "EUR" && (
        <div
          style={{
            fontSize: 12,
            color: "var(--muted-foreground)",
            marginTop: 4,
          }}
        >
          Original: {fmtPlain(inv.totalGross || total, inv.currency)}{" "}
          {inv.exchangeRate ? `@ ${inv.exchangeRate}` : ""}
        </div>
      )}
    </div>
  );
}

function PaymentRow({ p, onOpen }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "28px 110px 1fr 180px 110px",
          gap: 12,
          alignItems: "center",
        }}
      >
        <CreditCard size={18} />
        <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
          {p.date ? new Date(p.date).toLocaleDateString("es-ES") : "—"}
        </div>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {p.supplierName || "Proveedor"} — {p.refBank || "Referencia"}
        </div>
        <div
          style={{
            fontFamily: "monospace",
            fontWeight: 700,
            textAlign: "right",
          }}
        >
          {p.currency && p.currency !== "EUR"
            ? fmtPlain(p.amount || 0, p.currency)
            : fmtEUR(p.amount || 0)}
        </div>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button
            className="button button--ghost"
            onClick={() => onOpen(p)}
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ======= Página principal =======

export default function PaymentsPage({ projectCode }) {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("unpaid"); // unpaid | paid
  const [q, setQ] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | partial | overdue | paid
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [showUpload, setShowUpload] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [toast, setToast] = useState(null);

  // Sync data: invoices from DashboardPage
  useEffect(() => {
    if (!projectCode) return;
    const unsub1 = onSnapshot(
      collection(db, "projects", projectCode, "invoices"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // merge payments? We'll only compute with payments collection; invoices can carry embedded payments if you hydrate backend; here we keep as is.
        setInvoices(list);
      }
    );
    const unsub2 = onSnapshot(
      collection(db, "projects", projectCode, "payments"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPayments(list);
      }
    );
    return () => {
      unsub1?.();
      unsub2?.();
    };
  }, [projectCode]);

  // Build invoice->payments relation in-memory
  const invoicesWithPayments = useMemo(() => {
    if (payments.length === 0)
      return invoices.map((i) => ({ ...i, payments: i.payments || [] }));
    const byInv = {};
    payments.forEach((p) => {
      (p.allocations || []).forEach((a) => {
        if (!byInv[a.invoiceId]) byInv[a.invoiceId] = [];
        byInv[a.invoiceId].push({
          id: p.id,
          date: p.date,
          amount: a.amount,
          currency: p.currency,
          exchangeRate: p.exchangeRate,
          refBank: p.refBank,
          type: p.type,
          fileUrl: p.fileUrl,
        });
      });
      // If no allocations but invoice ids array exists, split evenly or full?
      if (
        (!p.allocations || p.allocations.length === 0) &&
        Array.isArray(p.invoices) &&
        p.invoices.length > 0
      ) {
        const per = Number(p.amount || 0) / p.invoices.length;
        p.invoices.forEach((id) => {
          if (!byInv[id]) byInv[id] = [];
          byInv[id].push({
            id: p.id,
            date: p.date,
            amount: per,
            currency: p.currency,
            exchangeRate: p.exchangeRate,
            refBank: p.refBank,
            type: p.type,
            fileUrl: p.fileUrl,
          });
        });
      }
    });
    return invoices.map((i) => ({
      ...i,
      payments: [...(i.payments || []), ...(byInv[i.id] || [])],
    }));
  }, [invoices, payments]);

  // Filters
  const filteredInvoices = useMemo(() => {
    let items = invoicesWithPayments.slice();

    // search provider
    const qq = q.trim().toLowerCase();
    if (qq) {
      items = items.filter(
        (i) =>
          (i.supplierName || i.supplier || "").toLowerCase().includes(qq) ||
          (i.number || i.invoiceNumber || i.id || "").toLowerCase().includes(qq)
      );
    }

    // currency filter
    if (currencyFilter !== "all") {
      items = items.filter((i) => (i.currency || "EUR") === currencyFilter);
    }

    // due date range applies to invoices only
    if (dueFrom) {
      items = items.filter((i) =>
        !i.dueDate || i.dueDate >= dueFrom ? true : false
      );
    }
    if (dueTo) {
      items = items.filter((i) =>
        !i.dueDate || i.dueDate <= dueTo ? true : false
      );
    }

    // status filter
    if (statusFilter !== "all") {
      items = items.filter((i) => computeStatus(i) === statusFilter);
    }

    return items;
  }, [invoicesWithPayments, q, currencyFilter, dueFrom, dueTo, statusFilter]);

  const paidInvoices = useMemo(
    () => filteredInvoices.filter((i) => computeStatus(i) === "paid"),
    [filteredInvoices]
  );
  const unpaidInvoices = useMemo(
    () =>
      filteredInvoices.filter((i) =>
        ["pending", "partial", "overdue"].includes(computeStatus(i))
      ),
    [filteredInvoices]
  );

  // Payments filtered by provider search and currency; vencimiento no aplica
  const filteredPayments = useMemo(() => {
    let items = payments.slice();
    const qq = q.trim().toLowerCase();
    if (qq) {
      items = items.filter(
        (p) =>
          (p.supplierName || "").toLowerCase().includes(qq) ||
          (p.refBank || "").toLowerCase().includes(qq)
      );
    }
    if (currencyFilter !== "all") {
      items = items.filter((p) => (p.currency || "EUR") === currencyFilter);
    }
    return items;
  }, [payments, q, currencyFilter]);

  // Order: unpaid by due asc, paid by pay date desc; payments by date desc
  const unpaidOrdered = useMemo(() => {
    return unpaidInvoices.slice().sort((a, b) => {
      const ad = a.dueDate || "9999-12-31";
      const bd = b.dueDate || "9999-12-31";
      return ad.localeCompare(bd);
    });
  }, [unpaidInvoices]);

  const paidOrdered = useMemo(() => {
    return paidInvoices.slice().sort((a, b) => {
      const ad = (a.payments || [])[0]?.date || "0000-01-01";
      const bd = (b.payments || [])[0]?.date || "0000-01-01";
      return bd.localeCompare(ad);
    });
  }, [paidInvoices]);

  const paymentsOrdered = useMemo(() => {
    return filteredPayments
      .slice()
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [filteredPayments]);

  // Totals
  const totals = useMemo(() => {
    const totAll = sum(filteredInvoices, (i) => invoiceTotalEUR(i));
    const totPaid = sum(filteredInvoices, (i) => computePaidEUR(i));
    const totPending = Math.max(0, totAll - totPaid);
    const totOverdue = sum(
      filteredInvoices.filter((i) => computeStatus(i) === "overdue"),
      (i) => Math.max(0, invoiceTotalEUR(i) - computePaidEUR(i))
    );
    return { totAll, totPaid, totPending, totOverdue };
  }, [filteredInvoices]);

  // Pagination for visible list (by tab we pick dataset)
  const visibleList = useMemo(() => {
    if (tab === "unpaid") {
      // Mixed: invoices unpaid + recent payments (optional show)
      return unpaidOrdered;
    }
    // tab === "paid": paid invoices + payments list beneath in render
    return paidOrdered;
  }, [tab, unpaidOrdered, paidOrdered]);

  const totalPages = Math.max(1, Math.ceil(visibleList.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visibleList.slice(start, start + pageSize);
  }, [visibleList, page]);

  const currencyOptions = useMemo(() => {
    const set = new Set(
      (invoicesWithPayments || []).map((i) => i.currency || "EUR")
    );
    (payments || []).forEach((p) => set.add(p.currency || "EUR"));
    return ["all", ...Array.from(set)];
  }, [invoicesWithPayments, payments]);

  // Download Excel-compatible (CSV) for current filtered invoices by due date
  const downloadDueCSV = () => {
    // Only invoices from current filter set; when on unpaid tab it’s most relevant
    const rows = filteredInvoices
      .slice()
      .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
    const headers = [
      "Proveedor",
      "NumeroFactura",
      "Fecha",
      "Vencimiento",
      "TotalEUR",
      "PagadoEUR",
      "PendienteEUR",
      "Estado",
      "Moneda",
      "TotalOriginal",
      "Cambio",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      headers.join(","),
      ...rows.map((i) => {
        const total = invoiceTotalEUR(i);
        const paid = computePaidEUR(i);
        const pending = Math.max(0, total - paid);
        return [
          i.supplierName || i.supplier || "",
          i.number || i.invoiceNumber || i.id,
          i.issueDate || "",
          i.dueDate || "",
          (total || 0).toFixed(2),
          (paid || 0).toFixed(2),
          pending.toFixed(2),
          computeStatus(i),
          i.currency || "EUR",
          (i.totalGross || "").toString().replace(".", ","),
          i.exchangeRate || "",
        ]
          .map(esc)
          .join(",");
      }),
    ].join("\n");

    const blob = new Blob([lines], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vencimientos_${todayISO()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="container" style={{ padding: "20px 0 30px" }}>
      {/* Botones superiores compactos */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
          marginBottom: 16,
        }}
      >
        <button
          className="button"
          onClick={downloadDueCSV}
          title="Descargar vencimientos (CSV)"
          style={{
            padding: "6px 10px",
            fontSize: 13,
            height: 32,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <FileDown size={14} /> Descargar vencimientos
        </button>

        <button
          className="button button--primary"
          onClick={() => setShowUpload(true)}
          title="Registrar pago / justificante"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "6px 10px",
            fontSize: 13,
            backgroundColor: "#16a34a",
            color: "white",
            border: "none",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#15803d")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#16a34a")
          }
        >
          <Upload size={15} />
          Registrar pago
        </button>
      </div>

      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}
      >
        <div
          className="card"
          style={{
            display: "inline-flex",
            gap: 12,
            padding: 20,
            border: "1px solid var(--ring)",
            borderRadius: 12,
            alignItems: "center",
          }}
        >
          <div className="field" style={{ margin: 0, minWidth: 260 }}>
            <label style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Buscar
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Search size={16} />
              <input
                type="search"
                placeholder="Proveedor, ref o nº factura…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                style={{ width: 240 }}
              />
            </div>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Vencimiento desde
            </label>
            <input
              type="date"
              value={dueFrom}
              onChange={(e) => {
                setDueFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Vencimiento hasta
            </label>
            <input
              type="date"
              value={dueTo}
              onChange={(e) => {
                setDueTo(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="partial">Parcial</option>
              <option value="overdue">Vencido</option>
              <option value="paid">Pagado</option>
            </select>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Moneda
            </label>
            <select
              value={currencyFilter}
              onChange={(e) => {
                setCurrencyFilter(e.target.value);
                setPage(1);
              }}
            >
              {currencyOptions.map((c) => (
                <option key={c} value={c}>
                  {c === "all" ? "Todas" : c}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Totales */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Total facturado
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
              {fmtEUR(totals.totAll)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Total pagado
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
              {fmtEUR(totals.totPaid)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Total pendiente
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: 700 }}>
              {fmtEUR(totals.totPending)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              Total vencido
            </div>
            <div
              style={{
                fontFamily: "monospace",
                fontWeight: 700,
                color: "#dc2626",
              }}
            >
              {fmtEUR(totals.totOverdue)}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          className={`button ${tab === "unpaid" ? "button--primary" : ""}`}
          onClick={() => {
            setTab("unpaid");
            setPage(1);
          }}
        >
          No pagado
        </button>
        <button
          className={`button ${tab === "paid" ? "button--primary" : ""}`}
          onClick={() => {
            setTab("paid");
            setPage(1);
          }}
        >
          Pagado
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "var(--muted-foreground)",
            marginLeft: "auto",
          }}
        >
          <Filter size={14} /> {visibleList.length} resultados
        </div>
      </div>

      {/* List */}
      <div className="stack">
        {pageItems.length === 0 && (
          <p style={{ color: "var(--muted-foreground)" }}>
            No hay elementos para mostrar con el filtro actual.
          </p>
        )}

        {/* Unpaid tab: only invoices in pending/partial/overdue */}
        {tab === "unpaid" &&
          pageItems.map((i) => (
            <InvoiceRow
              key={i.id}
              inv={i}
              onOpen={(inv) => setInvoiceModal(inv)}
            />
          ))}

        {/* Paid tab: show paid invoices pageItems + a secondary list of recent payments below page */}
        {tab === "paid" &&
          pageItems.map((i) => (
            <InvoiceRow
              key={i.id}
              inv={i}
              onOpen={(inv) => setInvoiceModal(inv)}
            />
          ))}

        {/* Paid tab: payments section (always visible below, not paginated here for simplicity) */}
        {tab === "paid" && (
          <>
            <h4
              style={{
                margin: "12px 4px 0",
                color: "var(--muted-foreground)",
                fontWeight: 600,
              }}
            >
              Pagos registrados recientes
            </h4>
            <div className="stack">
              {paymentsOrdered.slice(0, 20).map((p) => (
                <PaymentRow
                  key={p.id}
                  p={p}
                  onOpen={(pay) => setPaymentModal(pay)}
                />
              ))}
              {paymentsOrdered.length === 0 && (
                <p style={{ color: "var(--muted-foreground)" }}>
                  No hay pagos registrados.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {visibleList.length > 0 && (
        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "center",
            gap: 12,
            alignItems: "center",
          }}
        >
          <button
            className="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            title="Anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 14 }}>
            Página {page} / {totalPages}
          </span>
          <button
            className="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            title="Siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Modales */}
      <PaymentUploadModal
        open={showUpload}
        onClose={() => setShowUpload(false)}
        projectCode={projectCode}
        invoices={invoicesWithPayments}
        defaultSupplier={q}
        onSaved={() => setToast({ msg: "Pago registrado correctamente" })}
      />

      <InvoiceDetailsModal
        open={!!invoiceModal}
        invoice={invoiceModal}
        onClose={() => setInvoiceModal(null)}
      />

      <PaymentDetailsModal
        open={!!paymentModal}
        payment={paymentModal}
        onClose={() => setPaymentModal(null)}
      />

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            background: toast.error ? "#dc2626" : "var(--accent)",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 10,
          }}
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
}
